import {
    Component,
    OnInit,
    ChangeDetectorRef,
    signal,
    OnDestroy,
    ChangeDetectionStrategy,
    WritableSignal,
} from "@angular/core";
import { Subject } from "rxjs";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { BudgetService } from "../budget.service";
import { WSOrganization } from "../../../../services/organization.service";
import { Budget } from "../Budget";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { S25IfShowDirective } from "../../../../standalone/s25.if.show.directive";
import { FormsModule } from "@angular/forms";
import { NgClass } from "@angular/common";
import { S25FadingSuccessMsgComponent } from "../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { BudgetAdminSummaryComponent } from "./budget.admin.summary.component";
import { BudgetAdminOrganizationsComponent } from "./budget.admin.organizations.component";
import { BudgetAdminRequestsComponent } from "./budget.admin.requests.component";
import { BudgetCategoryManagementComponent } from "./budget.category.management.component";
import { FiscalYearManagementComponent } from "./fiscal.year.management.component";
import { S25Util } from "../../../../util/s25-util";

interface FiscalYearSummary {
    fiscalYear: string;
    totalOrganizations: number;
    totalBudgetsCreated: number;
    totalAllocated: number;
    pendingRequests: number;
}

@TypeManagerDecorator("s25-ng-budget-admin")
@Component({
    selector: "s25-ng-budget-admin",
    template: `
        @if (init()) {
            <div class="budget-admin-container">
                <!-- Header Section -->
                <div class="admin-header">
                    <h1>Budget Administration</h1>
                    @if (availableFiscalYears()?.length > 0) {
                        <div class="fiscal-year-selector">
                            <label>Fiscal Year:</label>
                            <select
                                [(ngModel)]="selectedFiscalYear"
                                (ngModelChange)="onFiscalYearChange()"
                                class="fiscal-year-dropdown"
                            >
                                @for (year of availableFiscalYears(); track year) {
                                    <option [value]="year">{{ year }}</option>
                                }
                            </select>

                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Refresh'"
                                [onClick]="loadAdminData"
                                [isLoading]="isLoading()"
                            ></s25-ng-button>
                        </div>
                    } @else {
                        <div class="fiscal-year-selector">
                            <p>No fiscal years found. Please add a fiscal year to continue.</p>
                        </div>
                    }
                </div>

                <!-- Loading State -->
                @if (isLoading()) {
                    <s25-ng-loading-inline-static
                        [text]="'Loading budget administration data...'"
                    ></s25-ng-loading-inline-static>
                }

                <!-- Error State -->
                @if (error) {
                    <div class="error-message">
                        <p>{{ error }}</p>
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonText]="'Retry'"
                            [onClick]="loadAdminData"
                        ></s25-ng-button>
                    </div>
                }

                <!-- Success Message -->
                <s25-ng-fading-success-msg [message]="successMessage"></s25-ng-fading-success-msg>

                <!-- Main Content -->
                @if (!isLoading() && !error) {
                    <!-- Summary Cards -->
                    @if (fiscalYearSummary) {
                        <s25-ng-budget-admin-summary
                            [summary]="fiscalYearSummary"
                            [selectedFiscalYear]="selectedFiscalYear"
                        >
                        </s25-ng-budget-admin-summary>
                    } @else {
                        <div class="summary-placeholder">
                            <p>Unable to load fiscal year summary data</p>
                        </div>
                    }

                    <!-- Tab Navigation -->
                    <div class="tab-navigation">
                        <div class="tab-buttons">
                            <button
                                [ngClass]="{ active: activeTab() === 'organizations' }"
                                (click)="setActiveTab('organizations')"
                                class="tab-button"
                            >
                                Organizations & Budgets
                            </button>
                            <button
                                [ngClass]="{ active: activeTab() === 'requests' }"
                                (click)="setActiveTab('requests')"
                                class="tab-button"
                            >
                                Pending Requests
                                @if ((pendingRequests?.length || 0) > 0) {
                                    <span class="badge">{{ pendingRequests.length }}</span>
                                }
                            </button>
                            <button
                                [ngClass]="{ active: activeTab() === 'categories' }"
                                (click)="setActiveTab('categories')"
                                class="tab-button"
                            >
                                Categories
                            </button>
                            <button
                                [ngClass]="{ active: activeTab() === 'fiscal_years' }"
                                (click)="setActiveTab('fiscal_years')"
                                class="tab-button"
                            >
                                Fiscal Years
                            </button>
                        </div>
                    </div>

                    <!-- Organizations & Budgets Tab -->
                    <div *s25IfShow="activeTab() === 'organizations'" class="tab-content">
                        <s25-ng-budget-admin-organizations
                            [selectedFiscalYear]="selectedFiscalYear"
                        ></s25-ng-budget-admin-organizations>
                    </div>

                    <!-- Pending Requests Tab -->
                    <div *s25IfShow="activeTab() === 'requests'" class="tab-content">
                        <s25-ng-budget-admin-requests
                            [pendingRequests]="pendingRequests"
                            [selectedFiscalYear]="selectedFiscalYear"
                            (requestsProcessed)="onRequestsProcessed()"
                        >
                        </s25-ng-budget-admin-requests>
                    </div>

                    <!-- Categories Tab -->
                    <div *s25IfShow="activeTab() === 'categories'" class="tab-content">
                        <s25-ng-budget-category-management
                            (categoriesChanged)="onCategoriesChanged()"
                        ></s25-ng-budget-category-management>
                    </div>

                    <!-- Fiscal Years Tab -->
                    <div *s25IfShow="activeTab() === 'fiscal_years'" class="tab-content">
                        <s25-ng-fiscal-year-management
                            (fiscalYearsChanged)="onFiscalYearsChanged()"
                        ></s25-ng-fiscal-year-management>
                    </div>

                    <!-- Bulk Operations Tab -->
                    <div *s25IfShow="activeTab() === 'bulk'" class="tab-content">
                        <s25-ng-budget-admin-bulk
                            [organizations]="organizations"
                            [organizationTypes]="organizationTypes"
                            [selectedFiscalYear]="selectedFiscalYear"
                            (budgetsCreated)="onBudgetsCreated()"
                        >
                        </s25-ng-budget-admin-bulk>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .budget-admin-container {
            padding: 1.5rem;
            max-width: 1400px;
            margin: 0 auto;
        }

        .admin-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid #e5e5e5;
        }

        .admin-header h1 {
            margin: 0;
            color: #333;
            font-size: 2rem;
            font-weight: bold;
        }

        .fiscal-year-selector {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .fiscal-year-selector label {
            font-weight: 600;
            color: #666;
        }

        .fiscal-year-dropdown {
            padding: 0.5rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 1rem;
            min-width: 120px;
        }

        .error-message {
            text-align: center;
            padding: 2rem;
            background: #fff3f3;
            border: 1px solid #ffcdd2;
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message p {
            color: #d32f2f;
            margin-bottom: 1rem;
        }

        .tab-navigation {
            margin-bottom: 2rem;
        }

        .tab-buttons {
            display: flex;
            border-bottom: 2px solid #e5e5e5;
            gap: 0;
        }

        .tab-button {
            padding: 1rem 1.5rem;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 1rem;
            font-weight: 500;
            color: #666;
            border-bottom: 3px solid transparent;
            transition: all 0.2s ease;
            position: relative;
        }

        .tab-button:hover {
            color: #2196f3;
            background: #f8f9fa;
        }

        .tab-button.active {
            color: #2196f3;
            border-bottom-color: #2196f3;
            background: #f8f9fa;
        }

        .badge {
            background: #f44336;
            color: white;
            border-radius: 12px;
            padding: 0.2rem 0.5rem;
            font-size: 0.8rem;
            margin-left: 0.5rem;
        }

        .tab-content {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }

        .summary-placeholder {
            text-align: center;
            padding: 2rem;
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 8px;
            margin-bottom: 2rem;
            color: #856404;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
            .budget-admin-container {
                padding: 1rem;
            }

            .admin-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .fiscal-year-selector {
                justify-content: center;
            }

            .tab-buttons {
                flex-direction: column;
            }
        }
    `,
    standalone: true,
    imports: [
        S25LoadingInlineModule,
        S25ButtonComponent,
        S25IfShowDirective,
        FormsModule,
        NgClass,
        S25FadingSuccessMsgComponent,
        BudgetAdminSummaryComponent,
        BudgetAdminOrganizationsComponent,
        BudgetAdminRequestsComponent,
        BudgetCategoryManagementComponent,
        FiscalYearManagementComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetAdminComponent implements OnInit, OnDestroy {
    init = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    error: string | null = null;
    successMessage = signal<string>("");

    // Fiscal Year Management
    selectedFiscalYear: string = "";
    availableFiscalYears: WritableSignal<string[]> = signal<string[]>([]);
    fiscalYears: Budget.FiscalYear[] = [];

    // Tab Management
    activeTab: WritableSignal<"organizations" | "requests" | "categories" | "fiscal_years" | "bulk" | "history"> =
        signal("organizations");

    // Summary Data
    fiscalYearSummary: FiscalYearSummary | null = null;

    // Data for sub-components
    organizations: WSOrganization[] = []; // Keep for bulk operations
    organizationTypes: string[] = [];
    pendingRequests: Budget.BudgetRequest[] = [];

    private destroy$ = new Subject<void>();

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.initializeComponent();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @Bind
    private async initializeComponent(): Promise<void> {
        // Cancel any ongoing requests
        this.destroy$.next();

        // Reset component state
        this.init.set(false);
        this.isLoading.set(true);
        this.error = null;
        this.fiscalYearSummary = null;
        this.organizations = [];
        this.pendingRequests = [];

        this.cd.detectChanges();

        // Load fiscal years from database
        await this.loadFiscalYears();

        const [_, error] = await S25Util.Maybe(this.loadAdminData());

        if (error) {
            this.error = "Failed to load budget administration data. Please try again.";
        }

        this.init.set(true);
        this.isLoading.set(false);
        this.cd.detectChanges();
    }

    @Bind
    async loadAdminData(): Promise<void> {
        this.isLoading.set(true);
        this.error = null;
        this.cd.detectChanges();

        try {
            await this.loadFiscalYearSummary();
        } catch (error) {
            this.error = error instanceof Error ? error.message : "An unexpected error occurred";
            throw error;
        } finally {
            this.isLoading.set(false);
            this.cd.detectChanges();
        }
    }

    private async loadFiscalYears(): Promise<void> {
        const [fiscalYears, error] = await S25Util.Maybe(BudgetService.getFiscalYears());

        if (fiscalYears && fiscalYears.length > 0) {
            this.fiscalYears = fiscalYears;
            this.availableFiscalYears.set(fiscalYears.map((fy) => fy.name));

            // Select the current fiscal year by default
            const currentFiscalYear = fiscalYears.find((fy) => fy.isCurrent);
            if (currentFiscalYear) {
                this.selectedFiscalYear = currentFiscalYear.name;
            } else {
                // If no current fiscal year is set, use the first active one
                this.selectedFiscalYear = this.availableFiscalYears()?.[0] || "";
                this.cd.detectChanges();
            }
        } else {
            console.error("Failed to load fiscal years:", error);
            //If not fiscal years are loaded default to the fiscal years config since that required.
            this.activeTab.set("fiscal_years");
            this.cd.detectChanges();
        }
    }

    private async loadFiscalYearSummary(): Promise<void> {
        const [summaryData, error] = await S25Util.Maybe(
            BudgetService.getAdminBudgetSummary(this.selectedFiscalYear || ""),
        );

        if (error) {
            console.warn("Failed to load fiscal year summary:", error);
            this.fiscalYearSummary = null;
        } else {
            this.fiscalYearSummary = summaryData;
        }
    }

    onFiscalYearChange(): void {
        this.loadAdminData();
    }

    setActiveTab(tab: "organizations" | "requests" | "categories" | "fiscal_years" | "bulk" | "history"): void {
        this.activeTab.set(tab);
    }

    // Event handlers for sub-components
    @Bind
    onRequestsProcessed(): void {
        this.loadAdminData();
        this.successMessage.set("Budget requests processed successfully");
    }

    @Bind
    onBudgetsCreated(): void {
        // Refresh data after bulk budget creation
        this.loadAdminData();
        this.successMessage.set("Bulk budgets created successfully");
    }

    @Bind
    onCategoriesChanged(): void {
        // Categories have been updated, show success message
        this.successMessage.set("Categories updated successfully");
    }

    @Bind
    async onFiscalYearsChanged(): Promise<void> {
        // Fiscal years have been updated, reload them and refresh data
        await this.loadFiscalYears();
        await this.loadAdminData();
        this.successMessage.set("Fiscal years updated successfully");
    }
}
